// EnhancedDataProcessingView.jsx
import React, { useEffect } from "react";
import DataProcessingVM from "../viewmodel/IfsProcessingVM";
import { useLocation } from "react-router-dom";
import CTab from "../../../basecomp/newComponents/CTab";
import CStepper from "../../../basecomp/newComponents/CStepper";
import CUpload from "../../../basecomp/newComponents/CUpload";
import CLoader from "../../../basecomp/newComponents/CLoader";
import CCard from "../../../basecomp/newComponents/CCard";
import CDropdown from "../../../basecomp/newComponents/CDropdown";
import CAlert from "../../../basecomp/newComponents/CAlert";
import CButton from "../../../basecomp/newComponents/CButton";
import CHeader from "../../../basecomp/newComponents/CHeader";
import CBox from "../../../basecomp/newComponents/CBox";
import CContainer from "../../../basecomp/newComponents/CContainer";
import CPaper from "../../../basecomp/newComponents/CPaper";
import CText from "../../../basecomp/newComponents/CText";
import CDivider from "../../../basecomp/newComponents/CDivider";
import CGrid from "../../../basecomp/newComponents/CGrid";
import CThemeProvider from "../../../basecomp/newComponents/CThemeProvider";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CDialog from '../../../basecomp/components/CDialog';
import LanguageManagementModal from './LanguageMangementModal';
import { conasTheme } from "../../../basecomp/newComponents/CTheme";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LinearProgress, Typography } from '@mui/material';

const EnhancedDataProcessingView = () => {
  const {
    activeStep,
    selectedFile,
    isProcessing,
    selectedModel,
    processingProgress,
    processingStats,
    isMobile,
    totalCount,
    isUploading,
    uploadProgress,
    modelOptions,
    cardData,
    errorDetails,
    showErrorModal,
    cOpen,
    showLanguageModal,
    managedLanguages,
    availableLanguages,
    isTranslating,
    translationProgress,
    translationComplete,
    translationStats,
    showTranslationModal,
    duplicateDetails,
    showDuplicateModal,
    handleCloseDuplicateModal,
    handleFileUpload,
    handleStartProcessing,
    handleDownload,
    handleRequestSample,
    handleReset,
    handleModelChange,
    handleViewHistory,
    handleFileSelection,
    handleErrorDialogClose,
    handleManageLanguages,
    handleAddLanguage,
    handleDeleteLanguage,
    handleStartTranslation,
    handleCloseTranslationModal,
    setCOpen,
    setSelectedFile,
    setShowLanguageModal,
    clearAllIntervals,
  } = DataProcessingVM();

  const location = useLocation();

  // ─── Error dialog content ──────────────────────────────────────────────────
  const errorDialogContent = errorDetails && (
    <div style={{ marginTop: "-20px" }}>
      {errorDetails.message && (
        <div style={{ marginBottom: "16px" }}>
          <p style={{ fontWeight: "bold", color: "#D32F2F", fontSize: "18px", marginBottom: "8px" }}>
            Error:
          </p>
          <p style={{ fontSize: "16px" }}>{errorDetails.message}</p>
        </div>
      )}

      {errorDetails.missing_columns?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <p style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "8px" }}>Missing Required Columns:</p>
          <ul style={{ marginLeft: "20px" }}>
            {errorDetails.missing_columns.map((col, i) => (
              <li key={i} style={{ marginBottom: "4px", fontSize: "14px" }}>{col}</li>
            ))}
          </ul>
        </div>
      )}

      {errorDetails.extra_columns?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <p style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "8px" }}>Extra Columns:</p>
          <ul style={{ marginLeft: "20px" }}>
            {errorDetails.extra_columns.map((col, i) => (
              <li key={i} style={{ marginBottom: "4px", fontSize: "14px" }}>{col}</li>
            ))}
          </ul>
        </div>
      )}

      {errorDetails.expected_columns?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <p style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "8px" }}>Expected Columns:</p>
          <ul style={{ marginLeft: "20px" }}>
            {errorDetails.expected_columns.map((col, i) => (
              <li key={i} style={{ marginBottom: "4px", fontSize: "14px" }}>{col}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: "20px", fontSize: "15px", color: "#555" }}>
        <p>Please correct your file format and try uploading again.</p>
      </div>
    </div>
  );

  // ─── Duplicate dialog content ──────────────────────────────────────────────
  const duplicateDialogContent = duplicateDetails && (
    <div style={{ marginTop: "-8px" }}>
      {/* Summary banner */}
      <div
        style={{
          backgroundColor: "#fff8e1",
          border: "1px solid #ffe082",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        <span style={{ fontSize: "20px", lineHeight: 1.2 }}>⚠️</span>
        <div>
          <p style={{ margin: 0, fontWeight: "bold", fontSize: "14px", color: "#795548" }}>
            {duplicateDetails.total_duplicate_rows} duplicate row
            {duplicateDetails.total_duplicate_rows !== 1 ? "s" : ""} found across{" "}
            {duplicateDetails.unique_duplicate_values} unique material
            {duplicateDetails.unique_duplicate_values !== 1 ? "s" : ""}
          </p>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#8d6e63" }}>
            Only unique records will be processed. Review the details below.
          </p>
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          border: "1px solid #f0c040",
          borderRadius: "8px",
          overflow: "hidden",
          fontSize: "13px",
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 80px 2fr",
            backgroundColor: "#fff3cd",
            padding: "10px 16px",
            fontWeight: "bold",
            color: "#7a5c00",
            borderBottom: "1px solid #f0c040",
          }}
        >
          <span>Material</span>
          <span style={{ textAlign: "center" }}>Count</span>
          <span>Row Numbers</span>
        </div>

        {/* Data rows */}
        {duplicateDetails.duplicate_details.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 80px 2fr",
              padding: "10px 16px",
              backgroundColor: idx % 2 === 0 ? "#fffdf5" : "#ffffff",
              borderBottom:
                idx < duplicateDetails.duplicate_details.length - 1
                  ? "1px solid #f5e9c0"
                  : "none",
              alignItems: "center",
            }}
          >
            <span style={{ fontFamily: "monospace", color: "#333", fontSize: "13px" }}>
              {item.material}
            </span>
            <span
              style={{
                textAlign: "center",
                color: "#c0392b",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {item.count}
            </span>
            <span style={{ color: "#555" }}>
              Rows: {item.row_numbers.join(", ")}
            </span>
          </div>
        ))}
      </div>

      <p style={{ marginTop: "16px", fontSize: "12px", color: "#888", marginBottom: 0 }}>
        Processing will continue with unique records only. You can proceed to the next step.
      </p>
    </div>
  );

  // ─── Translation modal content ─────────────────────────────────────────────
  const translationModalContent = (
    <div style={{ padding: "20px 0" }}>
      <Typography
        variant="h6"
        gutterBottom
        style={{ marginBottom: "20px", textAlign: "center", fontWeight: "bold" }}
      >
        Translation in Progress
      </Typography>

      <div style={{ marginBottom: "30px" }}>
        <LinearProgress
          variant="determinate"
          value={translationProgress}
          style={{ height: "12px", borderRadius: "6px", backgroundColor: "#e0e0e0" }}
          sx={{
            "& .MuiLinearProgress-bar": {
              backgroundColor: translationProgress === 100 ? "#4caf50" : "#1976d2",
              transition: "transform 0.4s ease",
            },
          }}
        />
        <Typography
          variant="body1"
          style={{
            marginTop: "12px",
            textAlign: "center",
            fontSize: "18px",
            fontWeight: "bold",
            color: translationProgress === 100 ? "#4caf50" : "#1976d2",
          }}
        >
          {Math.round(translationProgress)}% Complete
        </Typography>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginTop: "20px",
          padding: "20px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
        }}
      >
        <div style={{ textAlign: "center", padding: "10px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "8px" }}>
            Records Processed
          </Typography>
          <Typography variant="h5" style={{ fontWeight: "bold", color: "#1976d2" }}>
            {translationStats.processed} / {translationStats.total}
          </Typography>
        </div>

        <div style={{ textAlign: "center", padding: "10px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "8px" }}>
            Percentage
          </Typography>
          <Typography variant="h5" style={{ fontWeight: "bold", color: "#1976d2" }}>
            {typeof translationStats.percentage === "number"
              ? translationStats.percentage.toFixed(1)
              : translationProgress.toFixed(1)}
            %
          </Typography>
        </div>

        <div style={{ textAlign: "center", padding: "10px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "8px" }}>
            Time Elapsed
          </Typography>
          <Typography variant="h5" style={{ fontWeight: "bold", color: "#666" }}>
            {translationStats.timeElapsed || "0:00"}
          </Typography>
        </div>

        <div style={{ textAlign: "center", padding: "10px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: "8px" }}>
            Time Remaining
          </Typography>
          <Typography variant="h5" style={{ fontWeight: "bold", color: "#666" }}>
            {translationStats.timeRemaining || "calculating..."}
          </Typography>
        </div>
      </div>

      <div
        style={{
          marginTop: "30px",
          textAlign: "center",
          padding: "15px",
          backgroundColor: "#e3f2fd",
          borderRadius: "8px",
          border: "1px solid #90caf9",
        }}
      >
        <Typography variant="body2" style={{ color: "#1565c0", fontSize: "14px" }}>
          <strong>Please wait while we translate your records...</strong>
        </Typography>
        <Typography
          variant="body2"
          style={{ color: "#1565c0", fontSize: "13px", marginTop: "5px" }}
        >
          This may take a few minutes depending on the number of records.
        </Typography>
      </div>

      {translationProgress === 100 && (
        <div
          style={{
            marginTop: "20px",
            textAlign: "center",
            padding: "15px",
            backgroundColor: "#e8f5e9",
            borderRadius: "8px",
            border: "1px solid #81c784",
          }}
        >
          <Typography variant="body1" style={{ color: "#2e7d32", fontWeight: "bold" }}>
            ✓ Translation Complete!
          </Typography>
        </div>
      )}
    </div>
  );

  // ─── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  // ─── File info panel (step 2) ──────────────────────────────────────────────
  const renderFileInfo = () => {
    const params = new URLSearchParams(location.search);
    const urlBatchId = params.get("batchId");
    const urlModelName = params.get("modelName");
    const urlTotalRecords = params.get("totalRecords");
    const urlToBeProcessed = params.get("toBeProcessed") === "true";

    if (urlBatchId && urlToBeProcessed) {
      const fileName = `IFS_Operations_${
        urlModelName ? urlModelName.replace(/[\s\-*]/g, "_") : ""
      }.xlsx`;
      return (
        <CPaper variant="outlined" sx={{ p: 3, mb: 3 }} theme={conasTheme}>
          <CText variant="subtitle1" gutterBottom theme={conasTheme}>
            File Information
          </CText>
          <CGrid container spacing={2} theme={conasTheme}>
            <CGrid item xs={12} sm={6}>
              <CText>
                <strong>Tool:</strong> IFS Operations
              </CText>
              <CText>
                <strong>File Name:</strong> {fileName}
              </CText>
            </CGrid>
            <CGrid item xs={12} sm={6}>
              <CText>
                <strong>Total Records:</strong> {urlTotalRecords || totalCount || 0}
              </CText>
              <CText>
                <strong>Status:</strong> Ready to process
              </CText>
            </CGrid>
          </CGrid>
        </CPaper>
      );
    }

    if (!selectedFile) return null;

    return (
      <CPaper variant="outlined" sx={{ p: 3, mb: 3 }} theme={conasTheme}>
        <CText variant="subtitle1" gutterBottom theme={conasTheme}>
          File Information
        </CText>
        <CGrid container spacing={2}>
          <CGrid item xs={12} sm={6}>
            <CText>
              <strong>Tool:</strong> IFS Operations
            </CText>
            <CText>
              <strong>File Name:</strong> {selectedFile?.name}
            </CText>
          </CGrid>
          <CGrid item xs={12} sm={6}>
            <CText>
              <strong>Total Records:</strong> {totalCount || 0}
            </CText>
            <CText>
              <strong>Status:</strong> Ready to process
            </CText>
          </CGrid>
        </CGrid>
      </CPaper>
    );
  };

  const handleModalClose = () => {
    handleErrorDialogClose();
    setCOpen(false);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <CBackDrop cOpen={cOpen} cHandleClose={() => setCOpen(false)} cAllowClickToClose={false} />

      {/* File format error dialog */}
      {errorDetails && (
        <CDialog
          cOpen={showErrorModal}
          cHandleClose={handleModalClose}
          cTitle="File Format Error"
          cDialogContent={errorDialogContent}
          cDialogWidth="sm"
          error
          cDialogActions={[{ label: "Close", onClick: handleModalClose, color: "primary" }]}
        />
      )}

      {/* Duplicate records warning dialog */}
      {duplicateDetails && (
        <CDialog
          cOpen={showDuplicateModal}
          cHandleClose={handleCloseDuplicateModal}
          cTitle="Duplicate Records Detected"
          cDialogContent={duplicateDialogContent}
          cDialogWidth="sm"
          cDialogActions={[
            {
              label: "Got it, Continue",
              onClick: handleCloseDuplicateModal,
              color: "primary",
            },
          ]}
        />
      )}

      {/* Translation progress dialog */}
      <CDialog
        cOpen={showTranslationModal}
        cHandleClose={() => {}}
        cTitle="Translation Progress"
        cDialogContent={translationModalContent}
        cDialogWidth="sm"
        cDialogActions={
          isTranslating
            ? []
            : [{ label: "Close", onClick: handleCloseTranslationModal, color: "primary" }]
        }
      />

      {/* Language management modal */}
      <LanguageManagementModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        languages={managedLanguages}
        availableLanguages={availableLanguages}
        onAddLanguage={handleAddLanguage}
        onDeleteLanguage={handleDeleteLanguage}
      />

      {/* Main content */}
      <CThemeProvider theme={conasTheme}>
        <CBox
          sx={{ bgcolor: conasTheme.palette.background.default, minHeight: "100vh" }}
          theme={conasTheme}
        >
          <CContainer maxWidth="lg" sx={{ pt: 8, pb: 5 }} theme={conasTheme}>
            <CPaper elevation={3} sx={{ p: 3, borderRadius: 2 }} theme={conasTheme}>
              <CStepper activeStep={activeStep} isMobile={isMobile} theme={conasTheme} />

              {/* ── Step 1: Upload ── */}
              {activeStep === 1 && (
                <CBox sx={{ mt: 3 }} theme={conasTheme}>
                  {/* Top controls row */}
                  <CBox
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      gap: 2,
                      mb: 3,
                    }}
                    theme={conasTheme}
                  >
                    <CBox
                      sx={{ width: { xs: "100%", sm: "200px" }, pb: { sm: 0.5 } }}
                      theme={conasTheme}
                    >
                      <CButton
                        onClick={handleManageLanguages}
                        type="custom"
                        label="Manage Languages"
                        sx={{ width: "100%" }}
                        iconPosition="start"
                        disabled={isUploading}
                        size={isMobile ? "small" : "medium"}
                      />
                    </CBox>

                    <CBox sx={{ width: { xs: "100%", sm: "200px" } }} theme={conasTheme}>
                      <CDropdown
                        selectedValue={selectedModel}
                        onValueChange={handleModelChange}
                        options={modelOptions || []}
                        label="Language Selection"
                        theme={conasTheme}
                        sx={{ width: "100%" }}
                        showSearch={false}
                        clearable={false}
                      />
                    </CBox>

                    <CBox
                      sx={{ width: { xs: "100%", sm: "200px" }, pb: { sm: 0.5 } }}
                      theme={conasTheme}
                    >
                      <CButton
                        onClick={handleViewHistory}
                        type="history"
                        label="History"
                        sx={{ width: "100%" }}
                        iconPosition="start"
                        disabled={isUploading}
                        size={isMobile ? "small" : "medium"}
                      />
                    </CBox>
                  </CBox>

                  {/* File upload zone */}
                  <CUpload
                    onFileSelection={handleFileSelection}
                    theme={conasTheme}
                    maxRecords="100,000"
                    selectedFile={selectedFile}
                    onClearFile={() => setSelectedFile(null)}
                    disabled={isUploading}
                    modelSelected
                    disableClearButton={isUploading}
                  />

                  {/* Upload progress loader */}
                  {isUploading && (
                    <CLoader
                      progress={uploadProgress}
                      stats={processingStats}
                      status="Uploading"
                      theme={conasTheme}
                    />
                  )}

                  <CDivider sx={{ my: 2 }} theme={conasTheme} />

                  {/* Bottom action row */}
                  <CBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                    theme={conasTheme}
                  >
                    <CButton
                      type="downloadSample"
                      onClick={handleRequestSample}
                      disabled={isUploading}
                      iconPosition="start"
                      size={isMobile ? "small" : "medium"}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />

                    <CButton
                      type="upload"
                      onClick={handleFileUpload}
                      disabled={!selectedFile || isUploading}
                      iconPosition="start"
                      isUploading={isUploading}
                      size={isMobile ? "small" : "medium"}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                  </CBox>
                </CBox>
              )}

              {/* ── Step 2: Process ── */}
              {activeStep === 2 && (
                <CBox sx={{ mt: 3 }} theme={conasTheme}>
                  <CAlert
                    severity="success"
                    message="File uploaded successfully! You can now start the processing."
                    theme={conasTheme}
                  />

                  {renderFileInfo()}

                  {isProcessing && (
                    <CLoader
                      progress={processingProgress}
                      stats={processingStats}
                      status="Processing"
                      theme={conasTheme}
                    />
                  )}

                  <CBox sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <CButton type="back" onClick={handleReset} disabled={isProcessing} />
                    <CButton
                      type="process"
                      onClick={handleStartProcessing}
                      disabled={isProcessing}
                      isProcessing={isProcessing}
                    />
                  </CBox>
                </CBox>
              )}

              {/* ── Step 3: Download ── */}
              {activeStep === 3 && (
                <CBox sx={{ mt: 3 }} theme={conasTheme}>
                  <CAlert
                    severity="success"
                    message="Processing complete! You can now download the results."
                    theme={conasTheme}
                  />

                  {translationComplete && (
                    <CBox sx={{ mb: 3 }}>
                      <CAlert
                        severity="success"
                        message="Translation completed successfully!"
                        theme={conasTheme}
                      />
                    </CBox>
                  )}

                  <CPaper variant="outlined" sx={{ p: 3, mb: 3 }} theme={conasTheme}>
                    <CHeader
                      title="Processing Summary"
                      variant="subtitle1"
                      gutterBottom
                      theme={conasTheme}
                    />
                    <CCard cards={cardData || []} theme={conasTheme} />
                  </CPaper>

                  <CBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <CButton type="reset" onClick={handleReset} disabled={isTranslating} />

                    <CButton
                      type="download"
                      onClick={handleDownload}
                      iconPosition="start"
                      disabled={isTranslating}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                  </CBox>
                </CBox>
              )}
            </CPaper>
          </CContainer>
        </CBox>
      </CThemeProvider>
    </>
  );
};

export default EnhancedDataProcessingView;